// Single home for capture capability decisions (per the Tier-B spec): which sink
// tier a browser gets, which MP4 mime MediaRecorder should record, and the Tier-B
// export dims. Dependency-injected (like recorder.ts resolveDeps) so jsdom tests
// can exercise the full platform matrix.
import { Mp4Recorder, type RecorderDeps } from "./recorder";
import { aspectDims, type FrameDims } from "./compositor";
import type { AspectRatio } from "../types";

export type CaptureTier = "webcodecs" | "mediarecorder";

export interface MediaRecorderStaticLike {
  isTypeSupported?: (mime: string) => boolean;
}

export interface SupportDeps {
  /** Forwarded to Mp4Recorder.isSupported (Tier-A leg). */
  recorderDeps?: Partial<RecorderDeps>;
  /** Default: globalThis.MediaRecorder. Pass null to simulate absence. */
  MediaRecorder?: MediaRecorderStaticLike | null;
  /** Default: probe HTMLCanvasElement.prototype.captureStream. */
  canvasCaptureStream?: boolean;
}

// Probe order: High@4.0 → Main → Baseline → container-only. iOS Safari answers the
// explicit-codecs form; the bare form is the last-ditch catch for probes that reject
// codec strings while still recording H.264/AAC MP4.
const MP4_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.640028,mp4a.40.2",
  "video/mp4;codecs=avc1.4d0028,mp4a.40.2",
  "video/mp4;codecs=avc1.42e01e,mp4a.40.2",
  "video/mp4",
];

function defaultMediaRecorder(): MediaRecorderStaticLike | null {
  const g = globalThis as { MediaRecorder?: MediaRecorderStaticLike };
  return g.MediaRecorder ?? null;
}

function defaultCanvasCaptureStream(): boolean {
  return typeof HTMLCanvasElement !== "undefined" &&
    typeof (HTMLCanvasElement.prototype as { captureStream?: unknown }).captureStream === "function";
}

/** First recordable MP4 mime, or null (⇒ Tier-B impossible — MP4-only decision). */
export function pickMp4Mime(mr: MediaRecorderStaticLike | null = defaultMediaRecorder()): string | null {
  if (!mr || typeof mr.isTypeSupported !== "function") return null;
  for (const mime of MP4_MIME_CANDIDATES) {
    try { if (mr.isTypeSupported(mime)) return mime; } catch { /* try next */ }
  }
  return null;
}

/** Tiered export gate: WebCodecs (Tier-A) → MediaRecorder+captureStream+MP4 (Tier-B) → null. */
export function detectCaptureTier(deps?: SupportDeps): CaptureTier | null {
  if (Mp4Recorder.isSupported(deps?.recorderDeps)) return "webcodecs";
  const mr = deps && "MediaRecorder" in deps ? deps.MediaRecorder ?? null : defaultMediaRecorder();
  const capture = deps?.canvasCaptureStream ?? defaultCanvasCaptureStream();
  if (mr && capture && pickMp4Mime(mr) !== null) return "mediarecorder";
  return null;
}

/** Tier-B export resolution (720p tier). Tier-A keeps compositor.aspectDims (1080p). */
export function tierBDims(aspect: AspectRatio): FrameDims {
  return aspect === "16:9" ? { w: 1280, h: 720 } : { w: 720, h: 1280 };
}

export interface DimsProbeDeps {
  now?: () => number;
  makeCanvas?: () => HTMLCanvasElement;
  frames?: number;
  budgetMs?: number;
}

// Cached per session: the device's Tier-B resolution tier. iOS exposes no trustworthy
// deviceMemory/hardwareConcurrency, so we MEASURE: composite a few synthetic frames at
// 1080p with the real per-frame op mix and time them.
let cachedDimsTier: "1080" | "720" | null = null;

export function __resetDimsProbeCacheForTests(): void { cachedDimsTier = null; }

function drawBenchFrame(ctx: CanvasRenderingContext2D, src: HTMLCanvasElement): void {
  // Same op mix as a real composited frame: blurred+darkened cover draw (backdrop),
  // main image, two overlay draws (slide-out/doodle/Rive stand-ins), caption text.
  ctx.filter = "blur(24px) brightness(0.4)";
  ctx.drawImage(src, 0, 0, 1080, 1920);
  ctx.filter = "none";
  ctx.drawImage(src, 0, 200, 1080, 1520);
  ctx.drawImage(src, 100, 300, 400, 400);
  ctx.drawImage(src, 500, 1200, 300, 300);
  ctx.font = "48px sans-serif";
  ctx.fillText("The quick brown fox jumps over the lazy dog", 40, 1700);
}

/** Tier-B export dims: 1080p when this device demonstrably composites fast enough, else 720p. */
export async function probeExportDims(aspect: AspectRatio, deps?: DimsProbeDeps): Promise<FrameDims> {
  const pick = (tier: "1080" | "720"): FrameDims => (tier === "1080" ? aspectDims(aspect) : tierBDims(aspect));
  if (cachedDimsTier) return pick(cachedDimsTier);
  const now = deps?.now ?? (() => performance.now());
  const frames = deps?.frames ?? 6;
  const budgetMs = deps?.budgetMs ?? 8; // 30fps ≈ 33ms/frame; ≤8ms leaves encode+playback headroom
  const makeCanvas = deps?.makeCanvas ?? (() => document.createElement("canvas"));
  try {
    const canvas = makeCanvas();
    canvas.width = 1080; canvas.height = 1920; // 2MP — orientation-agnostic cost
    const ctx = canvas.getContext("2d");
    const src = makeCanvas();
    src.width = 1080; src.height = 1920;
    src.getContext("2d")?.fillRect(0, 0, 1080, 1920);
    if (!ctx) { cachedDimsTier = "720"; return pick("720"); }
    drawBenchFrame(ctx, src); // warm-up frame pays one-time allocation costs
    const t0 = now();
    let elapsed = 0;
    let drawn = 0;
    // Early bail: once cumulative time exceeds the whole budget (frames × budgetMs),
    // the average can no longer come in under budget — stop burning main-thread time
    // on exactly the slow devices the 720p tier exists for.
    while (drawn < frames) {
      drawBenchFrame(ctx, src);
      drawn++;
      elapsed = now() - t0;
      if (elapsed > frames * budgetMs) break;
    }
    cachedDimsTier = elapsed / drawn <= budgetMs ? "1080" : "720";
  } catch { cachedDimsTier = "720"; }
  return pick(cachedDimsTier);
}
